import { useEffect, useState } from 'react';
import { useContent } from '../data/ContentContext';
import { FeatureIcon } from '../components/icons';
import './featurePage.css';
import './DataIntelligencePage.css';

/** Data Intelligence product portfolio cards (Tableau-style section). */
const portfolio = [
  {
    title: 'Supplier Quality Management Data Dashboard',
    description:
      'End-to-end visibility into supplier quality — incoming inspection, process control, and Soc test data consolidated for early risk detection and performance tracking.',
    linkText: 'Open dashboard',
  },
  {
    title: 'Problem Solving Data Analysis Dashboard',
    description:
      'Turn PCA, FATP, and IQC findings into actionable insight — surface recurring defects and root causes to accelerate corrective action.',
    linkText: 'Open dashboard',
  },
  {
    title: 'NPI Ramping Data Management Dashboard',
    description:
      'Track new product introduction and ramp health — yield trends, IPQC SPC, and OBA/NUD metrics in one place to keep ramps on target.',
    linkText: 'Open dashboard',
  },
];

interface QualityMetric {
  id: string;
  table: string;
  column: string;
  value: number;
}

interface MetricsResponse {
  file: string;
  updated: string | null;
  metrics: QualityMetric[];
}

/** Turn a raw table name like "combinefatp_material_issue" into a readable label. */
function prettyLabel(table: string): string {
  return table
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Business-friendly display label for a KPI card, matched from the raw table
 * name. Falls back to a prettified table name for anything unmapped.
 */
function metricLabel(table: string): string {
  const t = table.toLowerCase();
  if (t.includes('material_issue') || t.includes('material issue'))
    return 'FATP Material issue solved';
  if (t.includes('iqc')) return 'FATP IQC Pass';
  if (t.includes('pca')) return 'PCA Findings in suppliers';
  if (t.includes('oba')) return 'OBA review in suppliers';
  if (t.includes('nud')) return 'NUD Identification';
  return prettyLabel(table);
}

/** A single digit that rolls downward (odometer-style) into place on mount. */
function RollingDigit({ digit, delay }: { digit: number; delay: number }) {
  const [rolled, setRolled] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setRolled(true), 40 + delay);
    return () => clearTimeout(t);
  }, [delay]);

  // Strip = two cycles of 0-9 (20 rows, each 1em tall). Start on the
  // second-cycle target digit, settle on the first-cycle target digit, so the
  // reel scrolls one full turn *downward* and lands on the correct number.
  const startY = -(10 + digit);
  const endY = -digit;

  return (
    <span className="di-roll">
      <span
        className="di-roll__strip"
        style={{ transform: `translateY(${rolled ? endY : startY}em)` }}
      >
        {Array.from({ length: 20 }).map((_, i) => (
          <span className="di-roll__digit" key={i}>
            {i % 10}
          </span>
        ))}
      </span>
    </span>
  );
}

/** Renders a formatted number (with thousands separators) as rolling digits. */
function RollingNumber({ value }: { value: number }) {
  const text = value.toLocaleString();
  let digitIndex = 0;
  return (
    <span className="di-roll-num">
      {text.split('').map((ch, i) => {
        if (/\d/.test(ch)) {
          const delay = digitIndex * 90;
          digitIndex += 1;
          return <RollingDigit key={i} digit={Number(ch)} delay={delay} />;
        }
        return (
          <span className="di-roll-sep" key={i}>
            {ch}
          </span>
        );
      })}
    </span>
  );
}

function useQualityMetrics() {
  const [metrics, setMetrics] = useState<QualityMetric[] | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch('/api/dashboard/metrics', {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as MetricsResponse;
        if (!cancelled) {
          setMetrics(data.metrics ?? []);
          setState('ready');
        }
      } catch {
        if (!cancelled) setState('error');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { metrics, state };
}

export function DataIntelligencePage() {
  const { dataIntelligenceContent } = useContent();
  const { hero, howItWorks, examples, launchUrl } = dataIntelligenceContent;
  const { metrics, state: metricsState } = useQualityMetrics();

  return (
    <div className="page">
      {/* Hero — Tableau-style banner */}
      <section className="di-hero">
        <div className="di-hero__content">
          <h1 className="di-hero__title">{hero.subtitle}</h1>
          <p className="di-hero__subtitle">{hero.description}</p>
        </div>
        <div className="di-hero__art" aria-hidden>
          {/* Decorative background shapes */}
          <span className="di-hero__blob di-hero__blob--1" />
          <span className="di-hero__blob di-hero__blob--2" />
          <span className="di-hero__blob di-hero__blob--3" />
          <span className="di-hero__spark di-hero__spark--1">✦</span>
          <span className="di-hero__spark di-hero__spark--2">✦</span>
          <span className="di-hero__plus di-hero__plus--1">+</span>
          <span className="di-hero__plus di-hero__plus--2">+</span>

          {/* Floating dashboard screenshot */}
          <div className="di-hero__screen">
            <img
              src="/pillars/dashboard.png"
              alt=""
              className="di-hero__img"
            />
          </div>

          {/* Floating agent conversation */}
          <div className="di-hero__chat">
            <div className="di-hero__chat-head">
              <span className="di-hero__chat-label">DAP-AI Agent</span>
              <span className="di-hero__avatar" />
            </div>
            <div className="di-hero__bubble">
              Show me supplier yield trends broken down by defect
            </div>
            <div className="di-hero__answer">
              <span className="di-hero__answer-tag">› Analyzed</span>
              <p>
                Processed 1,270 records across 20+ dashboards and flagged the
                top quality risks automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality metric KPI cards — live from S3 CSV */}
      <div className="di-metrics">
        {metricsState === 'loading' &&
          Array.from({ length: 5 }).map((_, i) => (
            <div className="di-metric di-metric--skeleton" key={i} aria-hidden>
              <div className="di-metric__value" />
              <div className="di-metric__label" />
            </div>
          ))}

        {metricsState === 'error' && (
          <div className="di-metric di-metric--error">
            <p>Unable to load quality metrics right now. Please refresh.</p>
          </div>
        )}

        {metricsState === 'ready' &&
          (metrics ?? []).map((m) => (
            <div className="di-metric" key={m.id}>
              <div className="di-metric__value">
                <RollingNumber value={m.value} />
              </div>
              <div className="di-metric__label">{metricLabel(m.table)}</div>
              <div className="di-metric__sub">{m.column}</div>
            </div>
          ))}
      </div>

      {/* How it works */}
      <section className="feat-card">
        <h2 className="feat-card__title">
          <FeatureIcon name="clipboard" /> How It Works
        </h2>
        <div className="di-how">
          <div className="di-how__card di-how__card--before">
            <div className="di-how__label di-how__label--before">✕ Before</div>
            <p>{howItWorks.before}</p>
          </div>
          <div className="di-how__arrow" aria-hidden>
            →
          </div>
          <div className="di-how__card di-how__card--after">
            <div className="di-how__label di-how__label--after">
              ✓ After (DAP-AI)
            </div>
            <p>{howItWorks.after}</p>
          </div>
        </div>
      </section>

      {/* Product portfolio — Tableau-style */}
      <section className="di-portfolio">
        <h2 className="di-portfolio__title">
          Explore the Data Intelligence Product Portfolio
        </h2>
        <div className="di-portfolio__grid">
          {portfolio.map((item) => (
            <div className="di-portfolio__card" key={item.title}>
              <span className="di-portfolio__accent" aria-hidden />
              <h3 className="di-portfolio__name">{item.title}</h3>
              <p className="di-portfolio__desc">{item.description}</p>
              <a
                className="di-portfolio__link"
                href={launchUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.linkText}
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Dashboard examples */}
      <section className="feat-card">
        <h2 className="feat-card__title">
          <FeatureIcon name="bar-chart" /> Dashboard Examples
        </h2>
        <div className="di-examples">
          {examples.map((name) => (
            <div className="di-example" key={name}>
              <span className="di-example__icon" aria-hidden>
                <FeatureIcon name="line-chart" size={17} />
              </span>
              {name}
            </div>
          ))}
        </div>
      </section>

      {/* Launch */}
      <a
        className="di-launch"
        href={launchUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="di-launch__btn">
          <FeatureIcon name="bar-chart" size={18} /> Launch Dashboards
        </span>
        <span className="di-launch__note">
          Opens the Tableau dashboard portal in a new tab
        </span>
      </a>
    </div>
  );
}
